/**
 * Édition d'un exercice (`ARCHITECTURE_V2.md` §1.1 et §4.1, specs §12.1).
 *
 * 🔴 **Un exercice se crée SANS image, et c'est le cas nominal.** Le §1.1 le dit
 * en toutes lettres : « l'utilisateur peut créer un exercice sans image — c'est
 * le cas courant pour une machine propre à sa salle. Aucun média n'est
 * obligatoire, et **l'écran ne réserve pas d'espace vide quand il n'y en a
 * pas** ». Conséquence concrète, et c'est ce qui distingue cet éditeur d'un
 * formulaire ordinaire : il n'y a **aucun champ d'image**, aucun cadre gris,
 * aucun « ajouter une photo » désactivé. Les images viennent du jeu du domaine
 * public déposé sur le serveur (`infra/fetch-exercises.sh`) ; il n'existe aucun
 * chemin par lequel un exercice personnel en recevrait, donc rien à montrer.
 *
 * ⚠️ **Un exercice de base s'édite aussi**, et il garde sa provenance : `source`
 * est déduite par le serveur et n'est jamais modifiable (§4.1 — « une
 * provenance, jamais un état »). Corriger « Barbell Squat » en « Squat barre »
 * ne le transforme pas en exercice personnel, et lui laisse ses deux
 * illustrations.
 *
 * ⚠️ **`clientId` est tiré à l'ouverture, pas à l'enregistrement**, et c'est
 * ce qui rend la création composable hors ligne (§2.3-b) : l'adresse existe
 * avant l'écriture, donc une routine créée dans la foulée (étape 5) sait déjà
 * vers quoi pointer.
 */

import type { ExerciseLevel } from '../../../../shared/types';
import { Button } from '../../components/nourri';
import { Field, Hint, Sheet } from '../../components/ui/primitives';
import {
  canSaveExercise,
  type ExerciseDraft,
} from './exerciseDraft';

/** Libellés français des trois valeurs fermées du jeu importé. */
const NIVEAUX: readonly { value: ExerciseLevel; label: string }[] = [
  { value: 'beginner', label: 'Débutant' },
  { value: 'intermediate', label: 'Intermédiaire' },
  { value: 'expert', label: 'Confirmé' },
];

const FORCES: readonly { value: ExerciseDraft['force']; label: string }[] = [
  { value: '', label: '— non précisé —' },
  { value: 'push', label: 'Poussée' },
  { value: 'pull', label: 'Tirage' },
  { value: 'static', label: 'Statique' },
];

const MECANIQUES: readonly { value: ExerciseDraft['mechanic']; label: string }[] = [
  { value: '', label: '— non précisé —' },
  { value: 'compound', label: 'Polyarticulaire' },
  { value: 'isolation', label: 'Isolation' },
];

function Select<T extends string>({
  label,
  value,
  options,
  onChange,
}: {
  label: string;
  value: T;
  options: readonly { value: T; label: string }[];
  onChange: (value: T) => void;
}) {
  return (
    <label style={{ display: 'flex', flexDirection: 'column', gap: '0.375rem' }}>
      <span className="n-field__label">{label}</span>
      <select
        value={value}
        onChange={(event) => {
          onChange(event.target.value as T);
        }}
        className="ui-select"
      >
        {options.map((option) => (
          <option key={option.value} value={option.value}>
            {option.label}
          </option>
        ))}
      </select>
    </label>
  );
}

export function ExerciseEditorSheet({
  open,
  title,
  draft,
  onDraftChange,
  saving,
  onSubmit,
  onClose,
}: {
  open: boolean;
  title: string;
  draft: ExerciseDraft;
  onDraftChange: (next: ExerciseDraft) => void;
  saving: boolean;
  onSubmit: () => void;
  onClose: () => void;
}) {
  const canSubmit = canSaveExercise(draft);

  return (
    <Sheet open={open} title={title} onClose={onClose}>
      <Field
        label="Nom"
        value={draft.name}
        onChange={(name) => {
          onDraftChange({ ...draft, name });
        }}
        placeholder="Presse à cuisses"
      />

      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(2, minmax(0, 1fr))', gap: '0.6rem' }}>
        <Field
          label="Catégorie"
          value={draft.category}
          onChange={(category) => {
            onDraftChange({ ...draft, category });
          }}
          placeholder="musculation"
        />
        <Field
          label="Matériel"
          value={draft.equipment}
          onChange={(equipment) => {
            onDraftChange({ ...draft, equipment });
          }}
          placeholder="machine"
        />
      </div>

      <Select
        label="Niveau"
        value={draft.level}
        options={NIVEAUX}
        onChange={(level) => {
          onDraftChange({ ...draft, level });
        }}
      />

      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(2, minmax(0, 1fr))', gap: '0.6rem' }}>
        <Select
          label="Type d’effort"
          value={draft.force}
          options={FORCES}
          onChange={(force) => {
            onDraftChange({ ...draft, force });
          }}
        />
        <Select
          label="Mécanique"
          value={draft.mechanic}
          options={MECANIQUES}
          onChange={(mechanic) => {
            onDraftChange({ ...draft, mechanic });
          }}
        />
      </div>

      {/*
        ⚠️ Saisis en clair, séparés par des virgules — pas de sélecteur fermé.
        Le jeu importé est **traduit à l'import** depuis le 29/08/2026
        (`services/exerciseFr.ts`), donc son vocabulaire est français ; mais
        rien n'oblige quelqu'un qui note sa propre machine à s'y plier. Ces
        valeurs alimentent le filtre de la liste, qui se construit à partir de
        ce qui est **réellement employé** (`exerciseFacets`) — c'est ce qui
        permet à un vocabulaire libre de coexister avec celui du jeu.
      */}
      <Field
        label="Muscles principaux — séparés par des virgules"
        value={draft.primaryMuscles}
        onChange={(primaryMuscles) => {
          onDraftChange({ ...draft, primaryMuscles });
        }}
        placeholder="quadriceps, fessiers"
      />
      <Field
        label="Muscles secondaires"
        value={draft.secondaryMuscles}
        onChange={(secondaryMuscles) => {
          onDraftChange({ ...draft, secondaryMuscles });
        }}
        placeholder="ischio-jambiers"
      />

      <label style={{ display: 'flex', flexDirection: 'column', gap: '0.375rem' }}>
        <span className="n-field__label">Exécution — une étape par ligne</span>
        <textarea
          value={draft.instructions}
          onChange={(event) => {
            onDraftChange({ ...draft, instructions: event.target.value });
          }}
          rows={5}
          placeholder={'Régler le dossier\nPousser sans verrouiller les genoux\nRevenir lentement'}
          className="ui-textarea"
        />
      </label>

      <Button
        variant="primary"
        shape="rounded"
        block
        disabled={!canSubmit || saving}
        onClick={onSubmit}
      >
        {saving ? 'Enregistrement…' : 'Enregistrer l’exercice'}
      </Button>

      <Hint>
        Les exercices que vous créez n’ont pas d’illustration : les images
        viennent du jeu libre de droits installé sur le serveur. Rien d’autre ne
        les distingue — ils se cherchent, se filtrent et s’emploient pareil.
      </Hint>
    </Sheet>
  );
}
